"use client";

import { useRef, useEffect } from "react";
import Link from "next/link";
import { useCurrentUser } from "@/lib/hooks/useCurrentUser";
import { useTitle } from "@/lib/hooks/useTitle";
import { useHomeFeed } from "@/lib/hooks/useHomeFeed";
import { useFrozenFeedOrder } from "@/lib/hooks/useFrozenFeedOrder";
import { PostCard } from "@/components/PostCard";
import { GroupSidebar } from "@/components/GroupSidebar";
import { FeedAside } from "@/components/FeedAside";
import type { DayCounts } from "@/lib/changelog";
import { ProfileCompletionNudge } from "@/components/ProfileCompletionNudge";

export default function FeedClient({ recentDays }: { recentDays: DayCounts[] }) {
  useTitle("Feed");
  const { data: user } = useCurrentUser();
  const {
    data,
    // NOT `dataUpdatedAt`: that stamp bumps on every poll completion, and a
    // tracked prop that moves per tick re-renders this component and every
    // card under it on content-unchanged ticks — the per-poll churn the
    // freeze design forbids (OSO-622). The boolean flips exactly once.
    isFetchedAfterMount,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useHomeFeed("newest");

  const observerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = observerRef.current;
    if (!el || !hasNextPage) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // `FeedEntry` wraps the card with the timestamp the feed was sorted on, so
  // the sort key is the server's and no client re-sorts what it was handed —
  // the freeze layer below only PINS the order it was handed for the length of
  // the visit, it never invents one (OSO-622; see useFrozenFeedOrder.ts for
  // the self-bump story and every rule the pin encodes).
  const live = data?.pages.flatMap((page) => page.items) ?? [];
  const { entries, pendingCount, reveal } = useFrozenFeedOrder(live, isFetchedAfterMount);

  // The empty-feed welcome block already says "rediger din profil". Two
  // profile prompts stacked on one screen is exactly the nagging OSO-430 was
  // supposed to avoid, so the nudge stands down while that block is up.
  const showWelcome = !isLoading && entries.length === 0 && !!user;

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-950">
      <div className="max-w-[1400px] mx-auto flex gap-4 px-2 py-3 sm:px-4 sm:py-4">
        <div className="hidden lg:block w-72 shrink-0">
          <div className="sticky top-[4.5rem]">
            <GroupSidebar />
          </div>
        </div>

        <div className="flex-1 min-w-0 max-w-2xl">
          {/* "Nye opslag": the frozen order's release valve (OSO-622). Sticky at
              the same offset as the sidebar so it rides above the cards while
              scrolled. The wrapper stays mounted (and empty = zero height,
              outside the space-y stack) so the aria-live region exists BEFORE
              the pill appears — screen readers only announce changes inside an
              already-present live region. */}
          <div aria-live="polite" className="sticky top-[4.5rem] z-10 flex justify-center">
            {pendingCount > 0 && (
              <button
                type="button"
                onClick={reveal}
                className="mb-3 rounded-full bg-primary text-primary-foreground hover:bg-primary-hover px-4 py-1.5 text-sm font-medium shadow-md transition-colors"
              >
                {pendingCount === 1 ? "1 nyt opslag" : `${pendingCount} nye opslag`}
              </button>
            )}
          </div>
          <div className="space-y-4">
          {!showWelcome && <ProfileCompletionNudge />}

          {isLoading &&
            Array.from({ length: 3 }).map((_, i) => (
              <div
                key={i}
                className="bg-surface rounded-xl border border-border p-4 animate-pulse"
              >
                <div className="flex gap-3">
                  <div className="w-10 h-10 rounded-full bg-muted" />
                  <div className="flex-1">
                    <div className="h-3 bg-muted rounded w-1/3" />
                    <div className="h-4 bg-muted rounded w-2/3 mt-2" />
                    <div className="h-3 bg-muted rounded w-full mt-2" />
                  </div>
                </div>
              </div>
            ))}

          {entries.map((entry) => (
            /* `highlightUnread` is the HOME feed's alone (OSO-622 §6):
               GroupFeedClient deliberately does not pass it — group cards get
               the quiet shrink through the card's own `variant`, but never
               highlights and never dwell marks. `freshAfterMount` rides along
               so the unread baseline waits for the first post-mount fetch,
               exactly as the frozen order above does — a warm remount's cached
               `viewerLastSeenAt` predates the marks the last visit wrote. */
            <PostCard
              key={`post-${entry.post.id}`}
              post={entry.post}
              showGroup
              highlightUnread
              freshAfterMount={isFetchedAfterMount}
            />
          ))}

          <div ref={observerRef} className="h-10" />

          {isFetchingNextPage && (
            <div className="text-center text-subtle-foreground text-sm py-4">
              Indlæser flere opslag...
            </div>
          )}

          {showWelcome && user && (
            <div className="bg-surface rounded-xl border border-border p-6">
              <h2 className="text-lg font-semibold text-foreground mb-1">
                Velkommen til Theta, {user.name}!
              </h2>
              <p className="text-sm text-muted-foreground mb-5">
                Dit feed er tomt, fordi du ikke er medlem af nogen grupper
                endnu. Kom i gang med disse trin:
              </p>
              <div className="space-y-3">
                <Link
                  href={`/profil/${user.id}`}
                  className="flex items-center gap-3 rounded-lg border border-border p-3 hover:bg-surface-2 transition-colors"
                >
                  <div className="flex items-center justify-center w-9 h-9 rounded-full bg-primary-muted/50 text-primary shrink-0">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                      <circle cx="12" cy="7" r="4" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">Rediger din profil</p>
                    <p className="text-xs text-muted-foreground">Tilføj et billede og fortæl lidt om dig selv</p>
                  </div>
                </Link>
                <Link
                  href="/grupper"
                  className="flex items-center gap-3 rounded-lg border border-border p-3 hover:bg-surface-2 transition-colors"
                >
                  <div className="flex items-center justify-center w-9 h-9 rounded-full bg-purple-100 dark:bg-purple-900/50 text-purple-600 dark:text-purple-400 shrink-0">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                      <circle cx="9" cy="7" r="4" />
                      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">Udforsk grupper</p>
                    <p className="text-xs text-muted-foreground">Find og bliv medlem af grupper der interesserer dig</p>
                  </div>
                </Link>
              </div>
            </div>
          )}
          </div>
        </div>

        <FeedAside days={recentDays} />

      </div>
    </div>
  );
}
